/**
 * "Store listings" — an arche's EXTRA Store listings (ADR-035).
 *
 * Each extra listing is another entry point into the same arche — a game
 * inside it, like Flappyland's Wordtrace at `/games/wordtrace` — with its own
 * title, description, icon (also its Home Screen icon) and, for a game,
 * genres (for anything else, its Arche Store categories). It shows in Store
 * search, on the Games page, and on the arche's Store page.
 *
 * A listing uses a SLOT: $50 once (or one of the user's free listings);
 * retiring a listing frees its slot for another. The price shown is the price
 * charged — the form sends it back (`expectedFeeCents`) and the server refuses
 * a different one. Every submission passes a content check before anything is
 * charged; a refused one shows its reasons and costs nothing but the check.
 * One whose icon the check was unsure of waits for a person ("Waiting for
 * review"): a new one already holds its slot, refunded if it isn't approved,
 * and can be withdrawn (ADR-035 §12).
 *
 * Renders nothing for someone who cannot publish the arche (the endpoint 403s).
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ExternalLink, Gamepad2, Loader2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AppCategoryFields } from '@/components/AppCategoryFields';
import { GameListingFields, type GameListingValue } from '@/components/GameListingFields';
import { appCategoryLabel } from '@/lib/app-categories';
import { gameGenreLabel } from '@/lib/game-genres';
import {
  ApiError,
  type StoreListingReviewWire,
  type StoreListingWire,
  type StoreListingsOverview,
} from '@/lib/api';
import {
  uploadStoreListingIcon,
  useCreateStoreListing,
  useReorderStoreListings,
  useRestoreStoreListing,
  useRetireStoreListing,
  useStoreListings,
  useUpdateStoreListing,
  useWithdrawStoreListingReview,
} from '../hooks/useStoreListings';

/** Mirror the server's bounds (STORE_LISTING_* in @fias/db-types). */
const PATH_RE = /^\/[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)?$/;
const TITLE_MAX = 60;
const DESCRIPTION_MAX = 320;
const ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const ICON_MAX_BYTES = 512 * 1024;
const ICON_MIN_PX = 256;
const ICON_MAX_PX = 1024;

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** What the next slot costs, in words. */
function priceLine(quote: StoreListingsOverview['quote']): string {
  if (!quote.needsSlot) return 'Uses a free slot on this arche';
  if (quote.waived) return 'Uses one of your free listings';
  return `Adds a listing slot: ${dollars(quote.feeCents)} (${quote.feeCents.toLocaleString()} credits), once`;
}

/** Every reason a server refusal carries, or its message. */
function errorLines(err: unknown): string[] {
  if (err instanceof ApiError) {
    if (err.issues?.length) return err.issues.map((i) => i.message);
    return [err.message];
  }
  return [err instanceof Error ? err.message : 'Something went wrong. Try again.'];
}

/** Reads an image file's pixel size in the browser. */
function imageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error('That file is not an image this browser can read.'));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/** Why this file cannot be a listing icon, or null. */
export async function iconProblem(file: File): Promise<string | null> {
  if (!ICON_TYPES.includes(file.type)) return 'The icon must be a PNG, JPEG or WebP image.';
  if (file.size > ICON_MAX_BYTES) return 'The icon must be 512 KB or smaller.';
  try {
    const { width, height } = await imageSize(file);
    if (width !== height) return `The icon must be square (this one is ${width}×${height}).`;
    if (width < ICON_MIN_PX || width > ICON_MAX_PX) {
      return `The icon must be ${ICON_MIN_PX}–${ICON_MAX_PX} px square (this one is ${width} px).`;
    }
  } catch (err) {
    return (err as Error).message;
  }
  return null;
}

export function StoreListingsSection({ archeId }: { archeId: string }) {
  const { data, error } = useStoreListings(archeId);
  if (error instanceof ApiError && error.status === 403) return null;
  if (!data) return null;
  return <StoreListingsCard archeId={archeId} data={data} />;
}

type Editing = { mode: 'create' } | { mode: 'edit'; listing: StoreListingWire } | null;

function StoreListingsCard({ archeId, data }: { archeId: string; data: StoreListingsOverview }) {
  const [editing, setEditing] = useState<Editing>(null);
  const [restoring, setRestoring] = useState<StoreListingWire | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Builder and Forge link here as `#store-listings`; the router does not
  // scroll to a hash, and this card appears only once its data has loaded.
  useEffect(() => {
    if (window.location.hash === '#store-listings') {
      cardRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, []);
  const retire = useRetireStoreListing(archeId);
  const reorder = useReorderStoreListings(archeId);
  const withdraw = useWithdrawStoreListingReview(archeId);
  const live = data.listings.filter((l) => l.status === 'active');
  const retired = data.listings.filter((l) => l.status === 'retired');
  const reviews = data.reviews ?? [];
  const waiting = reviews.filter((r) => r.status === 'pending');
  const turnedDown = reviews.filter((r) => r.status === 'rejected' || r.status === 'withdrawn');
  const waitingEdit = new Set(waiting.flatMap((r) => (r.listingId ? [r.listingId] : [])));

  const move = (index: number, by: -1 | 1) => {
    const ids = live.map((l) => l.listingId);
    const target = index + by;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids);
  };

  return (
    <Card id="store-listings" ref={cardRef} className="mb-6 scroll-mt-20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Gamepad2 className="h-4 w-4" />
          Store listings
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Give a game — or any page — inside this arche its own place in the Arche Store and on the
          Games page, with its own icon (also used for “Add to Home Screen”). Each extra listing
          uses a slot: {dollars(data.slotFeeCents)} once. Retiring a listing frees its slot for
          another.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <span>
            <span className="font-medium">{data.slots.slots}</span> slot
            {data.slots.slots === 1 ? '' : 's'} · {data.slots.used} in use · {data.slots.free} free
            {data.waiversAvailable > 0 && (
              <span className="text-muted-foreground">
                {' '}
                · {data.waiversAvailable} free listing{data.waiversAvailable === 1 ? '' : 's'} on
                your account
              </span>
            )}
          </span>
          <Button size="sm" onClick={() => setEditing({ mode: 'create' })}>
            <Plus className="mr-1 h-4 w-4" />
            Add listing
          </Button>
        </div>

        {waiting.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-medium">Waiting for review</h3>
            <ul
              className="divide-y divide-border rounded-md border border-border"
              aria-label="Listings waiting for review"
            >
              {waiting.map((review) => (
                <ReviewRow key={review.reviewId} review={review}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={withdraw.isPending}
                    onClick={() => withdraw.mutate(review.reviewId)}
                  >
                    Withdraw
                  </Button>
                </ReviewRow>
              ))}
            </ul>
          </div>
        )}

        {live.length === 0 && retired.length === 0 && waiting.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No extra listings yet. The arche’s own listing is part of publishing it.
          </p>
        )}

        {live.length > 0 && (
          <ul
            className="divide-y divide-border rounded-md border border-border"
            aria-label="Live listings"
          >
            {live.map((listing, i) => (
              <ListingRow key={listing.listingId} listing={listing} archeId={archeId}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Move ${listing.title} up`}
                  disabled={i === 0 || reorder.isPending}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Move ${listing.title} down`}
                  disabled={i === live.length - 1 || reorder.isPending}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={waitingEdit.has(listing.listingId)}
                  title={
                    waitingEdit.has(listing.listingId)
                      ? 'A change to this listing is waiting for review'
                      : undefined
                  }
                  onClick={() => setEditing({ mode: 'edit', listing })}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={retire.isPending}
                  onClick={() => retire.mutate(listing.listingId)}
                >
                  Retire
                </Button>
              </ListingRow>
            ))}
          </ul>
        )}

        {retired.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-medium text-muted-foreground">Retired</h3>
            <ul
              className="divide-y divide-border rounded-md border border-border"
              aria-label="Retired listings"
            >
              {retired.map((listing) => (
                <ListingRow key={listing.listingId} listing={listing} archeId={archeId}>
                  <Button variant="outline" size="sm" onClick={() => setRestoring(listing)}>
                    Restore
                  </Button>
                </ListingRow>
              ))}
            </ul>
          </div>
        )}
        {turnedDown.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-medium text-muted-foreground">Not approved</h3>
            <ul
              className="divide-y divide-border rounded-md border border-border"
              aria-label="Listings not approved"
            >
              {turnedDown.map((review) => (
                <ReviewRow key={review.reviewId} review={review}>
                  {null}
                </ReviewRow>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      {editing && (
        <ListingDialog
          archeId={archeId}
          overview={data}
          editing={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {restoring && (
        <RestoreDialog
          archeId={archeId}
          listing={restoring}
          quote={data.quote}
          onClose={() => setRestoring(null)}
        />
      )}
    </Card>
  );
}

function ListingRow({
  listing,
  archeId,
  children,
}: {
  listing: StoreListingWire;
  archeId: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-center gap-3 p-3">
      <img
        src={listing.iconUrl}
        alt=""
        width={48}
        height={48}
        className="h-12 w-12 rounded-xl object-cover shadow-sm"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{listing.title}</span>
          <Badge variant={listing.status === 'active' ? 'secondary' : 'outline'}>
            {listing.status === 'active' ? 'Live' : 'Retired'}
          </Badge>
          {listing.isGame &&
            listing.genres.map((g) => (
              <Badge key={g} variant="outline" className="text-[10px]">
                {gameGenreLabel(g)}
              </Badge>
            ))}
          {!listing.isGame &&
            (listing.categories ?? []).map((c) => (
              <Badge key={c} variant="outline" className="text-[10px]">
                {appCategoryLabel(c)}
              </Badge>
            ))}
        </div>
        <a
          href={`/a/${archeId}${listing.path}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:underline"
        >
          {listing.path}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="flex items-center gap-1">{children}</div>
    </li>
  );
}

/** One submission a person decides: what it is, and where it stands. */
function ReviewRow({
  review,
  children,
}: {
  review: StoreListingReviewWire;
  children: React.ReactNode;
}) {
  const what = review.kind === 'edit' ? `Change to ${review.title}` : review.title;
  let note: string;
  if (review.status === 'pending') {
    note =
      'A person is checking the icon before it goes live.' +
      (review.boughtSlot ? ' The slot you bought is refunded if it isn’t approved.' : '');
  } else if (review.status === 'withdrawn') {
    note = 'You withdrew it.' + (review.boughtSlot ? ' The slot you bought was refunded.' : '');
  } else {
    const reason = review.decisionReason?.trim();
    note =
      (reason ? `Not approved: ${reason}${/[.!?]$/.test(reason) ? '' : '.'}` : 'Not approved.') +
      (review.boughtSlot ? ' The slot you bought was refunded.' : '');
  }
  return (
    <li className="flex flex-wrap items-center gap-3 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{what}</span>
          <Badge variant="outline">
            {review.status === 'pending'
              ? 'In review'
              : review.status === 'withdrawn'
                ? 'Withdrawn'
                : 'Not approved'}
          </Badge>
        </div>
        <p className="font-mono text-xs text-muted-foreground">{review.path}</p>
        <p className="text-xs text-muted-foreground">{note}</p>
      </div>
      <div className="flex items-center gap-1">{children}</div>
    </li>
  );
}

function ListingDialog({
  archeId,
  overview,
  editing,
  onClose,
}: {
  archeId: string;
  overview: StoreListingsOverview;
  editing: Exclude<Editing, null>;
  onClose: () => void;
}) {
  const existing = editing.mode === 'edit' ? editing.listing : null;
  const [path, setPath] = useState(existing?.path ?? '/');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [game, setGame] = useState<GameListingValue>({
    isGame: existing?.isGame ?? true,
    genres: existing?.genres ?? [],
  });
  const [categories, setCategories] = useState<string[]>(existing?.categories ?? []);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(existing?.iconUrl ?? null);
  const [iconError, setIconError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const create = useCreateStoreListing(archeId);
  const update = useUpdateStoreListing(archeId);

  useEffect(() => {
    if (!iconFile) return;
    const url = URL.createObjectURL(iconFile);
    setIconPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [iconFile]);

  const pathValid = PATH_RE.test(path);
  const valid =
    (existing !== null || pathValid) &&
    title.trim().length > 0 &&
    title.trim().length <= TITLE_MAX &&
    description.trim().length > 0 &&
    description.trim().length <= DESCRIPTION_MAX &&
    (!game.isGame || game.genres.length > 0) &&
    (existing !== null || iconFile !== null) &&
    !iconError;

  const onIcon = async (file: File | undefined) => {
    if (!file) return;
    const problem = await iconProblem(file);
    setIconError(problem);
    setIconFile(problem ? null : file);
  };

  const onSubmit = async () => {
    if (!valid) return;
    setBusy(true);
    setProblems([]);
    try {
      const iconStagingKey = iconFile ? await uploadStoreListingIcon(archeId, iconFile) : undefined;
      const content = {
        title: title.trim(),
        description: description.trim(),
        isGame: game.isGame,
        genres: game.isGame ? game.genres : [],
        categories: game.isGame ? [] : categories,
      };
      if (existing) {
        await update.mutateAsync({
          listingId: existing.listingId,
          input: { ...content, ...(iconStagingKey ? { iconStagingKey } : {}) },
        });
      } else {
        await create.mutateAsync({
          path,
          ...content,
          iconStagingKey: iconStagingKey!,
          expectedFeeCents: overview.quote.feeCents,
        });
      }
      onClose();
    } catch (err) {
      setProblems(
        err instanceof ApiError && err.code === 'QUOTE_CHANGED'
          ? ['The price changed while this was open. Check the new price below and try again.']
          : errorLines(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.title}` : 'Add a Store listing'}</DialogTitle>
          <DialogDescription>
            A game or page inside this arche, with its own place in the Store.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {!existing && (
            <div className="space-y-1.5">
              <Label htmlFor="listing-path">Path inside the arche</Label>
              <Input
                id="listing-path"
                value={path}
                onChange={(e) => setPath(e.target.value.trim())}
                placeholder="/games/wordtrace"
                aria-invalid={path !== '/' && !pathValid}
              />
              <p className="text-xs text-muted-foreground">
                One or two parts, like <span className="font-mono">/games/wordtrace</span>. It opens
                at{' '}
                <span className="font-mono">
                  /a/{archeId}
                  {pathValid ? path : '/…'}
                </span>
                {pathValid && (
                  <>
                    {' '}
                    —{' '}
                    <a
                      href={`/a/${archeId}${path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      test this link
                    </a>
                  </>
                )}
                .
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="listing-title">Title</Label>
            <Input
              id="listing-title"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Wordtrace"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="listing-description">Description</Label>
            <Textarea
              id="listing-description"
              value={description}
              maxLength={DESCRIPTION_MAX}
              rows={3}
              className="resize-none"
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Trace as many words as you can through a grid of letters."
            />
            <p className="text-right text-xs text-muted-foreground">
              {description.length}/{DESCRIPTION_MAX}
            </p>
          </div>
          <GameListingFields
            idPrefix="listing"
            value={game}
            onChange={setGame}
            maxGenres={overview.maxGenres}
          />
          {!game.isGame && (
            <AppCategoryFields idPrefix="listing" value={categories} onChange={setCategories} />
          )}
          <div className="space-y-1.5">
            <Label htmlFor="listing-icon">Icon</Label>
            <div className="flex items-center gap-3">
              {iconPreview ? (
                <img
                  src={iconPreview}
                  alt="Icon preview"
                  width={64}
                  height={64}
                  className="h-16 w-16 rounded-[22%] bg-white object-cover shadow"
                />
              ) : (
                <div className="h-16 w-16 rounded-[22%] border border-dashed border-border" />
              )}
              <div className="space-y-1">
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                  {iconPreview ? 'Change icon' : 'Choose icon'}
                </Button>
                <input
                  ref={fileRef}
                  id="listing-icon"
                  type="file"
                  accept={ICON_TYPES.join(',')}
                  className="sr-only"
                  onChange={(e) => void onIcon(e.target.files?.[0])}
                />
                <p className="text-xs text-muted-foreground">
                  Square PNG, JPEG or WebP, 512×512 recommended, up to 512 KB. Transparent areas
                  become white on a Home Screen.
                </p>
              </div>
            </div>
            {iconError && (
              <p role="alert" className="text-xs text-destructive">
                {iconError}
              </p>
            )}
          </div>
          {!existing && (
            <p className="rounded-md bg-muted px-3 py-2 text-sm">
              {priceLine(overview.quote)}. A content check (about 1 credit) runs first; nothing else
              is charged if it is refused.
            </p>
          )}
          {problems.length > 0 && (
            <ul role="alert" className="list-disc space-y-1 pl-5 text-sm text-destructive">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} disabled={!valid || busy}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {existing
              ? 'Save'
              : overview.quote.feeCents > 0
                ? `Add listing — ${dollars(overview.quote.feeCents)}`
                : 'Add listing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestoreDialog({
  archeId,
  listing,
  quote,
  onClose,
}: {
  archeId: string;
  listing: StoreListingWire;
  quote: StoreListingsOverview['quote'];
  onClose: () => void;
}) {
  const restore = useRestoreStoreListing(archeId);
  const [problems, setProblems] = useState<string[]>([]);
  const onRestore = async () => {
    setProblems([]);
    try {
      await restore.mutateAsync({ listingId: listing.listingId, expectedFeeCents: quote.feeCents });
      onClose();
    } catch (err) {
      setProblems(errorLines(err));
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !restore.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Restore {listing.title}?</DialogTitle>
          <DialogDescription>
            It goes back into the Store with the content it had.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="rounded-md bg-muted px-3 py-2 text-sm">{priceLine(quote)}.</p>
          {problems.length > 0 && (
            <ul role="alert" className="list-disc space-y-1 pl-5 text-sm text-destructive">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={restore.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void onRestore()} disabled={restore.isPending}>
            {restore.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {quote.feeCents > 0 ? `Restore — ${dollars(quote.feeCents)}` : 'Restore'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
